import { Alert, Row, Col, Card, Button, message, Modal, Divider, Collapse, Space, Typography } from "antd";
import {AppleFilled, GoogleOutlined, MailOutlined, LoadingOutlined, DownloadOutlined} from '@ant-design/icons';
import {
  formatDate,
  isSupportLead,
  isSupportAgent2,
  isSupportAgent3,
  isSupportManager,
  isDev,
  formatLicenseVersion
} from "../../../helpers/genericHelper";
import LoaderComponent from "../../Loader";
import CustomText from "../../CustomText";
import DuplicateUsers from "./DuplicateUsers";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import API from "../../../services/API";
import UpdateAccountModal from "./UpdateAccountModal";
import AdminChanges from "./AdminChanges";
import RegistrationDetails from "./RegistrationDetails";
import BlockHistory from "./BlockHistory";
import SillaDocuments from "./SillaDocuments";
import FileBankruptcy from "./Actions/FileBankruptcy";
import { GOOGLE_PROVIDER, EMAIL_PROVIDER, APPLE_PROVIDER } from "../../../constants/StringConstants";
import Data from "../../CustomText/data";
import ProfileActions from "./Profile/Actions";
import ProfileInfo from "./Profile/Info";
import SSNMatch from "./Profile/SSNMatch";
import PhoneNumberDisplay from "./Profile/PhoneNumberDisplay";
import AuditProfile from "./AuditProfile";
import DocumentVerificationLink from "./DocumentVerificationLink";
import DocumentVerifications from "./DocumentVerifications";
import HighRiskRules from "./HighRiskRules";
import AllowlistedRules from "./AllowlistedRules";
import RecentEmails from "./RecentEmails";
import { PersistedCollapse } from "../../PersistedSections";
import AutoPayAction from "./Actions/AutoPayAction";

const { Link, Text } = Typography;
const { Panel } = Collapse;

function ProfileSummary({
  userSummary = {},
  loading,
  refreshList,
  firebaseInfo,
  creditCards,
  autoPayStatus
}) {
  const {
    reportScores,
    dwollaCustomer,
    registrationRisk,
    adminChanges,
    reportErrors,
    subscriptions,
    saveAndBuildCredit
  } = userSummary;

  const [data, setData] = useState({});
  const [tos, setTOS] = useState([]);
  const [revealedSSN, setRevealedSSN] = useState(null);

  const [dataLoading, setDataLoading] = useState(true);

  const [modalVisible, setModalVisible] = useState(false);

  const [modalLoading, showModalLoading] = useState(false);

  const [disableAutoPayModalVisible, setDisableAutoPayModalVisible] = useState(false);

  const [modalForForcePullVisible, setModalForForcePullVisible] = useState(false);
  const [modalForReenrollExperianVisible, setModalForReenrollExperianVisible] = useState(false);

  const [modalForAutopayVisible, setModalForAutopayVisible] = useState(false);
  const [modalForceAutoPayLoading, showModalForceAutoPayLoading] = useState(false);

  const [blockHistoryLoading, setBlockHistoryLoading] = useState(false);

  const [modalWhitelistUserSSNVisible, setModalWhitelistUserSSNVisible] = useState(false);
  const [modalUndoWhitelistUserSSNVisible, setModalUndoWhitelistUserSSNVisible] = useState(false);

  const [modalForHistoryVisible, setModalForHistoryVisible] = useState();
  const [editAccountModalVisible, setEditAccountModalVisible] = useState(false);

  let { userId } = useParams();

  const [blockHistory, setBlockHistory] = useState([]);
  const [userBlockStatus, setUserBlockStatus] = useState(null);
  const [userBlockLoadingStatus, setUserBlockLoadingStatus] = useState(false);

  const [adverseActionNotices, setAdverseActionNotices] = useState([]);
  const [userFurnishingEvents, setUserFurnishingEvents] = useState([]);

  const reloadData = () => {
    if (refreshList) refreshList();
  }

  const getUserFurnishingEvents = async () => {
    const res = await API.getFurnishingEvents({ userId });
    setUserFurnishingEvents(res);
  }

  const getUserDetails = async (userId) => {
    try {
      setDataLoading(true);
      let res = await API.getUserSummary({ userId });
      setData(res);
    } catch (error) {
      message.error("Unable to fetch User Details");
    } finally {
      setDataLoading(false);
    }
  };

  const getTOS = async () => {
    try {
      let res = await API.getTOS({ userId });
      setTOS(res);
    } catch (error) {
      message.error("Unable to fetch User TOS");
    }
  };

  const getAdverseActionNotices = async (userId) => {
    try {
      const result = await API.getAdverseActionNotices(userId);
      setAdverseActionNotices(result);
    } catch (error) {
      message.error("Unable to fetch AANs")
    }
  }


  const revealSSN = async () => {
    try {
      const revealedSSNValue = await API.revealUserSSN({ userId })
      setRevealedSSN(revealedSSNValue);
    } catch (error) {
      message.error("Unable to fetch User SSN.");
    }
  }

  const enableBackupPayment = async () => {
    try {
      showModalLoading(true);
      await API.enableBackupPayment(userId);
      reloadData();
      message.success("Succesfully enabled the Backup Payment.");
      showModalLoading(false);
      setModalVisible(false);
    } catch (e) {
      message.error("Unable to enable the Backup Payment.");
      showModalLoading(false);
      setModalVisible(false);
    }
  };

  const disableAutoPay = async () => {
    try {
      showModalLoading(true);
      let response = await API.disableAutoPay(userId);
      message.success(response?.message);
      reloadData();
    } catch (e) {
      message.error("Unable to disable AutoPay.");
    }
    finally {
      showModalLoading(false);
      setDisableAutoPayModalVisible(false);
    }
  };

  const forceLogout = async () => {
    try {
      showModalLoading(true);
      await API.revokeLogin({ userId });
      message.success("Succesfully logged out the user. The user will be logged out from all devices in 1 hour.");
      showModalLoading(false);
      setModalVisible(false);
      reloadData();
    } catch (e) {
      message.error("Unable to log out the user.");
      showModalLoading(false);
      setModalVisible(false);
    }
  };

  const forceAutoPay = async () => {
    try {
      showModalForceAutoPayLoading(true);
      await API.forceAutoPay(userId);
      reloadData();
      message.success("Succesfully enabled the Autopay.");
      showModalForceAutoPayLoading(false);
      setModalForAutopayVisible(false);
    } catch (e) {
      message.error("Unable to enable the Autopay.");
      showModalForceAutoPayLoading(false);
      setModalForAutopayVisible(false);
    }
  };

  const userStatusResponse = async (userId) => {
    try {
      setUserBlockLoadingStatus(true);
      let userStatus = await API.getUserBlockedStatus({ userId });
      setUserBlockStatus(userStatus);
      setUserBlockLoadingStatus(false);
    } catch (error) {
      message.error("Unable to fetch User Status");
    }
  };

  const closeModal = () => {
    setModalVisible(false);
  };


  const closeDisableAutoPayModal = () => {
    setDisableAutoPayModalVisible(false);
  }

  const showEnableBackupPaymentModal = () => {
    setModalVisible(true);
  };

  const showForceAutoPayModal = () => {
    setModalForAutopayVisible(true);
  };

  const closeAutoPayModal = () => {
    setModalForAutopayVisible(false);
  };

  const loadBlockHistory = async () => {
    let res = await API.getBlockHistory(userId);
    setBlockHistory(res?.blockHistory);
  };

  const showHistoryPayModal = async () => {
    setModalForHistoryVisible(true);
    try {
      setBlockHistoryLoading(true);
      await loadBlockHistory();
    } catch (error) {
      message.error("Unable to fetch User Details");
    } finally {
      setBlockHistoryLoading(false);
    }
  };

  const closeBlockHistoryModal = () => {
    setModalForHistoryVisible(false);
  };

  const openEditAccountModal = () => {
    setEditAccountModalVisible(true);
  };

  const closeUpdateAccountModal = () => {
    setEditAccountModalVisible(false);
  };

  const handleShowForcePull = () => {
    setModalForForcePullVisible(true);
  }
  const closeForceReportPullModal = () => {
    setModalForForcePullVisible(false);
  }

  const forcefullyPullReport = async () => {
    try {
      showModalLoading(true);
      await API.forcefullyPullDocument(userId);
      message.success("Succesfully pulled the report.");
      showModalLoading(false);
      setModalForForcePullVisible(false);
      reloadData();
    } catch (e) {
      message.error("Unable to pull the report.");
      showModalLoading(false);
      setModalForForcePullVisible(false);
    }
  }

  const handleShowReenrollExperian = () => {
    setModalForReenrollExperianVisible(true);
  }
  const closeReenrollExperianModal = () => {
    setModalForReenrollExperianVisible(false);
  }

  const reenrollExperianUser = async () => {
    try {
      showModalLoading(true);
      await API.reenrollExperianUser(userId);
      message.success("Experian re-enrollment requested. Scheduled reports should resume; check back tomorrow, or use Force Pull if a report is needed now.");
      showModalLoading(false);
      setModalForReenrollExperianVisible(false);
    } catch (e) {
      message.error(e?.response?.data?.error_message || "Unable to request Experian re-enrollment.");
      showModalLoading(false);
      setModalForReenrollExperianVisible(false);
    }
  }

  const whitelistUserSSN = async () => {
    try {
      showModalLoading(true);
      await API.whitelistUserSSN(userId);
      message.success("Successfully whitelisted user's SSN");
      showModalLoading(false);
      closeWhitelistUserSSNModal();
      reloadData();
    } catch (e) {
      message.error(e?.response?.data?.error_message);
      showModalLoading(false);
      closeWhitelistUserSSNModal();
    }
  }

  const undoWhitelistUserSSN = async () => {
    try {
      showModalLoading(true);
      await API.undoWhitelistUserSSN(userId);
      message.success("Successfully removed whitelisted user's SSN");
      showModalLoading(false);
      closeUndoWhitelistUserSSNModal();
      reloadData();
    } catch (e) {
      message.error(e?.response?.data?.error_message);
      showModalLoading(false);
      closeUndoWhitelistUserSSNModal();
    }
  }


  const showWhitelistUserSSNModal = () => {
    setModalWhitelistUserSSNVisible(true);
  }

  const closeWhitelistUserSSNModal = () => {
    setModalWhitelistUserSSNVisible(false);
  }

  const showUndoWhitelistUserSSNModal = () => {
    setModalUndoWhitelistUserSSNVisible(true);
  }

  const closeUndoWhitelistUserSSNModal = () => {
    setModalUndoWhitelistUserSSNVisible(false);
  }

  const hasLoginProvider = (loginProvider) => {
    if (firebaseInfo?.providers) {
      return firebaseInfo.providers.some((provider) => provider.providerId === loginProvider);
    }
  }

  useEffect(() => {
    getTOS();
    userStatusResponse(userId);
    getUserDetails(userId);
    getAdverseActionNotices(userId);
    getUserFurnishingEvents();
    setBlockHistory([]);
    // Loaded up front for the count on the button. If it fails the button shows no count,
    // and opening the history retries and reports the error.
    loadBlockHistory().catch(() => {});
  }, [userId]);

  const {
    isUserBankrupt,
    silaUserStatus,
    avaUserStatus,
    bankruptcyFiledDate
  } = userBlockStatus || {};

  const subscription = subscriptions?.subscriptions?.find((sub) => sub.status === "ACTIVE") ?? subscriptions?.[0] ?? null;
  const creditCard = creditCards?.[0] ?? null;
  const savingsBuilder = userSummary?.sbAdminResponse?.savingBuilders?.[0] ?? null;
  
  return (
    <>
      {dataLoading && loading ? (
        <LoaderComponent />
      ) : (
        <Card bordered={false} className="criclebox tablespace mb-16">
          <Row>
            <Col span="16">
              <Space direction="vertical" style={{ width: "100%" }}>
                {isUserBankrupt && <Row>
                  <Col span="24">
                    <Alert showIcon message={`The user has filed for bankruptcy since ${formatDate(bankruptcyFiledDate)} and cannot be unblocked.`} type="error" />
                  </Col>
                </Row>}
                <Row>
                  <Data isTag tagColor={data?.status === "REGISTERED_USER" ? "success" : "warning"} title="Status" value={data?.status}/>
                  <Data isTag tagColor="processing" title="Date (Created)" value={formatDate(data?.creationDate, true)}/>
                  <Data isTag tagColor="processing" title="Date (Registered)" value={formatDate(data?.registrationTs, true)}/>
                  &nbsp;<Divider type="vertical" />&nbsp;
                  <Data isTag tagColor="processing" title="Sign Up Platform" value={data?.signUpPlatform?.toUpperCase()}/>
                  <Data isTag tagColor="processing" title="Login Provider" >
                    {
                      hasLoginProvider(APPLE_PROVIDER)
                      && <strong><AppleFilled title="Apple" /> <small>(Apple)</small></strong>}
                    {
                      hasLoginProvider(GOOGLE_PROVIDER)
                      && <strong><GoogleOutlined title="Google" style={{ color: "#4285F4" }} /> <small>(Google)</small></strong>}
                    {
                      hasLoginProvider(EMAIL_PROVIDER)
                      && <strong><MailOutlined title="Email" /> <small>(Email)</small></strong>}
                  </Data>
                  <Data isTag tagColor="processing" title="Device Type" value={data?.deviceType}/>
                </Row>
                <Row>
                  <Data isTag tagColor={subscription?.status === 'ACTIVE' ? "success" : "error"} title="Subscription Status" value={subscription?.status}/>
                  <Data isTag tagColor="processing" title="Subscription Plan" value={subscription?.interval}/>
                  &nbsp;<Divider type="vertical" />&nbsp;
                  <Data isTag tagColor="processing" title="Credit Card Status" value={creditCard?.accountStatus}/>
                  &nbsp;<Divider type="vertical" />&nbsp;
                  <Data isTag tagColor="processing" title="Savings Builder Status" value={savingsBuilder?.sbLoanEntry?.loanStatus}/>
                  &nbsp;<Divider type="vertical" />&nbsp;
                  <Data isTag tagColor="processing" title="Autopay Status" value={autoPayStatus?.isDisabled == true ? "DISABLED" : "ENABLED"}/>
                </Row>
              </Space>
            </Col>
            <Col span="4"></Col>
            <Col span="4">
              <ProfileActions
                reloadData={reloadData}
                data={data}
                saveAndBuildCredit={saveAndBuildCredit}
                userId={userId}
                userBlockLoadingStatus={userBlockLoadingStatus}
                userBlockStatus={userBlockStatus}
                userStatusResponse={userStatusResponse}
                openEditAccountModal={openEditAccountModal}
                forceLogout={forceLogout}
                furnishingEvents={userFurnishingEvents}
              />
            </Col>
          </Row>
          <Divider />
          <Row>
            <Col span="17">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Row>
                  <Col span="6">
                    <Data title="Full Name" value={`${data?.firstName} ${data?.lastName}`} copyable/>
                  </Col>
                  <Col span="6">
                    <Data title="Ava Account Number" value={data?.userId} copyable/>
                  </Col>
                  <Col span="6">
                    <Data title="Email (verified)" value={data?.email} copyable/>
                  </Col>
                  <Col span="6">
                    <Data title="SSN">
                      {revealedSSN ?? data?.ssn}&nbsp;
                      {isSupportAgent3() && <Link onClick={revealSSN}><small>(reveal)</small></Link>}
                      {!isSupportManager() && isSupportLead() && <SSNMatch />}
                    </Data>
                  </Col>
                </Row>
                <Row></Row>
                <Row>
                  <Col span="6">
                    <Data title="Date Of Birth" value={data?.dob}/>
                  </Col>
                  <Col span="6"
                    className="address-field"
                  >
                    <Data title="Address">
                      {(data.addressLineOne ||
                        data.addressLineTwo ||
                        data.city ||
                        data.state ||
                        data.zip) && (
                          <>
                            <p>
                              {`${data.addressLineOne || ""}`},{" "}
                              {`${data.addressLineTwo || ""}`}{" "}
                              <br />
                              {`${data.city || ""}, ${data.state || ""}`},{" "}
                              {`${data.zip || ""}`}
                            </p>
                          </>
                        )}
                      </Data>
                  </Col>
                  <Col span="6">
                    <PhoneNumberDisplay phoneNumber={data?.phoneNumber} />
                  </Col>
                  <Col span="6">
                    <Data title="IP Address" value={data?.creationIp}/>
                  </Col>
                </Row>
                <Row></Row>
                <Row>
                  <Col span="6">
                    <Data title="Agreement Acceptance Time (UTC)" value={formatDate(data?.tosAcceptanceDate)}/>
                  </Col>
                  <Col span="6">
                    <Data title="Registration risk level" value={registrationRisk ? registrationRisk.map((_risk) => _risk.riskLevel).join(",") : ""}/>
                  </Col>
                  <Col span="6">
                    <Data title="SSN Whitelisted" value={data?.ssnWhitelisted}/>
                  </Col>
                </Row>
                <Row></Row>
                <Row>
                  <Col span="5">
                    <Data title="SILA Status">
                      <Alert description={
                        <>
                          <b>{userBlockLoadingStatus ? <LoadingOutlined /> : (silaUserStatus?.isBlocked ? "BLOCKED" : "ACTIVE")}</b>
                        </>
                      } type={userBlockLoadingStatus ? "default" : (silaUserStatus?.isBlocked ? "error" : "success")} />
                    </Data>
                  </Col>
                  <Col span="1"></Col>
                  <Col span="5">
                    <Data title="AVA Status">
                      <Alert description={
                        <>
                          <b>{userBlockLoadingStatus ? <LoadingOutlined /> : (avaUserStatus?.isBlocked ? "BLOCKED" : "ACTIVE")}</b>
                        </>
                      } type={userBlockLoadingStatus ? "default" : (avaUserStatus?.isBlocked ? "error" : "success")} />
                    </Data>
                  </Col>
                </Row>
              </Space>
            </Col>
            <Col span="1">
              <Divider type="vertical" style={{ height: '100%' }}/>
            </Col>
            <Col span="6">
              <ProfileInfo subscriptions={subscriptions} all={userSummary} creditCards={creditCards} userId={userId} />
            </Col>
          </Row>
          <Divider />
          {data.status === "DELETED" && (
            <div style={{ marginBottom: 20, marginLeft: 10 }}>
              <Row>
                <h3 style={{ color: "#dc3545" }}>This user is deleted</h3>
              </Row>
              <Row>
                <h3>Reason:</h3>&nbsp;&nbsp;&nbsp;
                <p style={{ color: "#dc3545" }}>{data.deletedReason}</p>
              </Row>
            </div>
          )}
          <Row className={"card-bottom-buttons-container"}>

            {isSupportLead() &&
              <Button
                onClick={() => showEnableBackupPaymentModal()}
                className={"card-bottom-buttons"}
                type={"primary"}>
                {'Enable Backup Payment'}
              </Button>
            }

            {isSupportLead() &&
              <AutoPayAction autoPayStatusEnabled={!(autoPayStatus?.isDisabled == true)} refreshList={refreshList} type={null} userId={userId} />
            }

            {isSupportLead() &&
              <Button
                onClick={() => showForceAutoPayModal()}
                className={"card-bottom-buttons"}
                type={"primary"}>
                {'Run AutoPay'}
              </Button>
            }
            {(isSupportLead() && data.status !== "REGISTERED_USER" &&
              data.duplicateSsnEntries && data.duplicateSsnEntries.length > 0) && (
                <Button
                  disabled={data.ssnWhitelisted || data.duplicateSsnEntries.some((ssn) => ssn.isWhitelisted)}
                  onClick={() => showWhitelistUserSSNModal()}
                  className={"card-bottom-buttons"}
                  type={"primary"}>
                  {'Whitelist User\'s SSN'}
                </Button>
              )}
            {(isSupportLead() && data.status !== "REGISTERED_USER" &&
              data.duplicateSsnEntries && data.duplicateSsnEntries.length > 0 &&
              data.ssnWhitelisted === true
            ) && (
                <Button
                  danger
                  disabled={data.duplicateSsnEntries.some((ssn) => ssn.isWhitelisted)}
                  onClick={() => showUndoWhitelistUserSSNModal()}
                  className={"card-bottom-buttons"}
                  type={"primary"}>
                  {'Undo Whitelist User\'s SSN'}
                </Button>
              )}
              <AuditProfile reloadData={reloadData}
                userId={userId}
                modalLoading={modalLoading}
                showModalLoading={showModalLoading}
              />
              <DocumentVerificationLink reloadData={reloadData}
                userId={userId}
                modalLoading={modalLoading}
                showModalLoading={showModalLoading}
              />
            <Button
              onClick={() => showHistoryPayModal()}
              className={"card-bottom-buttons-secondary"}>
              View Block History{blockHistory?.length > 0 && ` (${blockHistory.length})`}
            </Button>

          </Row>

          <Card>
            <div style={{ padding: 10 }}>
              <h1
                style={{
                  color: "white",
                  backgroundColor: "#a448ff",
                  padding: 15,
                  borderRadius: 5,
                }}
              >
                Registration Details
              </h1>
              <RegistrationDetails reloadData={reloadData}
                registrationDetails={data.registrationDetails}
                registrationDiagnostics={data.registrationDiagnostics}
              />
            </div>
          </Card>
          
          <HighRiskRules userId={userId} />
          
          {data.duplicateSsnEntries &&
            data.duplicateSsnEntries.length > 0 && (
              <Card>
                <div style={{ padding: 10 }}>
                  <h1
                    style={{
                      color: "white",
                      backgroundColor: "#dc3545",
                      padding: 15,
                      borderRadius: 5,
                    }}
                  >
                    Duplicate Users
                  </h1>
                  <DuplicateUsers duplicateUsers={data.duplicateSsnEntries.map(entry => ({ value: entry.userId, isWhitelisted: entry.isWhitelisted }))} />
                </div>
              </Card>
            )}
          <Row style={{ marginLeft: 14, marginBottom: 14 }}>
            <Col xs={24} xl={24} sm={24} md={24}>
              <PersistedCollapse name="profile-sections">
                {/* ToS */}
                <Panel header="Terms of Service (acceptance time)" key="1">
                  <Space direction="vertical">
                    {tos && tos.length > 0 && tos.map((term) => (
                    <>
                      <Data>
                        <strong>{formatLicenseVersion(term.version ?? '-')}</strong>
                        <br />
                        <small>- {formatDate(term.createTs, true)}</small>
                      </Data>
                    </>
                  ))}
                  </Space>
                </Panel>
                {/* User Scores */}
                <Panel header="User Score" key="2">
                  {isSupportAgent3() &&
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 15 }}>
                      <Button
                        onClick={handleShowReenrollExperian}
                        style={{ width: '200px' }}>
                        {'Re-Enroll User'}
                      </Button>
                      <Button
                        onClick={handleShowForcePull}
                        type="primary" style={{ width: '200px' }}>
                        {'Force Pull'}
                      </Button>
                    </div>}
                  {reportScores && reportScores.length > 0 ? (
                    reportScores.map((_score) => {
                      return (
                        <Card
                          bordered={false}
                          className="criclebox tablespace mb-16"
                          key={_score.reportPullDate}
                        >
                          <Row
                            className="subscription-cancelled-card"
                            gutter={[30, 30]}
                            justify="space-between"
                          >
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Report Id</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"reportId"}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Score</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"score"}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Report Pull Date</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"reportPullDate"}
                                formatter={formatDate}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Last Ava Reported Date</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"latestAvaTradelineReportedDate"}
                                formatter={formatDate}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Total Ava Accounts</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"totalAvaAccounts"}
                              />
                            </Col>
                            { isDev() &&
                              <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <DownloadOutlined onClick={ () => downloadReport(userId, _score.reportId)} />
                              </Col>
                            }
                          </Row>
                        </Card>
                      );
                    })
                  ) : (
                    <Row className="no-data-card">No Data Found</Row>
                  )}
                </Panel>

                {/* Dwolla Customer */}
                <Panel header="Dwolla Customer" key="3">
                  <Card bordered={false} className="criclebox tablespace mb-16">
                    <Row
                      className="subscription-cancelled-card"
                      gutter={[30, 30]}
                    >
                      <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                        <h4>Dwolla Customer ID</h4>
                        {dwollaCustomer?.dwollaCustomerId ? (
                          <Text copyable className="value-text">
                            {dwollaCustomer.dwollaCustomerId}
                          </Text>
                        ) : (
                          <CustomText
                            dataSet={dwollaCustomer}
                            property1={"dwollaCustomerId"}
                          />
                        )}
                      </Col>

                      <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                        <h4>KYC Status</h4>
                        <CustomText
                          dataSet={dwollaCustomer}
                          property1={"kycStatus"}
                        />
                      </Col>

                      <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                        <h4>Created</h4>
                        <CustomText
                          dataSet={dwollaCustomer}
                          property1={"createdTs"}
                          formatter={(ts) => formatDate(ts, true)}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Panel>

                {isSupportAgent3() && <Panel header="Sila Documents" key="5">
                  <SillaDocuments userId={userId} />
                </Panel>}
                <Panel header="Admin Changes" key="6">
                  {adminChanges && adminChanges.length > 0 ? (
                    <AdminChanges adminChanges={adminChanges} />
                  ) : (
                    <></>
                  )}
                </Panel>
                <Panel header="Report Errors" key="7">
                  {reportErrors && reportErrors.length > 0 ? reportErrors.map((error) => {
                    return (
                      <Row className="subscription-cancelled-card" gutter={[30, 30]} key={error.error}>
                        <Col xs={24} sm={8} md={8} xl={8} xxl={8}>
                          <h4>Error</h4>
                          <CustomText dataSet={error} property1={'error'} />
                        </Col>
                        <Col xs={24} sm={8} md={8} xl={8} xxl={8}>
                          <h4>Detail</h4>
                          <CustomText dataSet={error} property1={'detail'} />
                        </Col>
                        <Col xs={24} sm={8} md={8} xl={8} xxl={8}>
                          <h4>Date</h4>
                          <CustomText dataSet={error}
                            property1={'reportPullTs'}
                            formatter={(val) => formatDate(val, true)} />
                        </Col>
                      </Row>
                    )
                  }) : (
                    <Row className="no-data-card">No Data Found</Row>
                  )}
                </Panel>
                <Panel header="Adverse Action Notices" key="8">
                  {adverseActionNotices && adverseActionNotices.length > 0 ? adverseActionNotices.map((notice) => (
                    <Card bordered={false} className="criclebox tablespace mb-16" key={notice.id}>
                      <Row className="subscription-cancelled-card" gutter={[30, 30]}>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="ID" value={notice.id} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Product" value={notice.product} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Created Date" value={formatDate(notice.createTs)} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Code" value={notice.adverseAction?.code} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Reason" value={notice.adverseAction?.reason} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Internal Cause" value={notice.adverseAction?.internalCause} />
                        </Col>
                      </Row>
                    </Card>
                  )) : (
                    <Row className="no-data-card">No Data Found</Row>
                  )}
                </Panel>
                <Panel header="Document Verifications" key="9">
                  <DocumentVerifications userId={userId} />
                </Panel>
                <Panel header="Allowlisted Rules" key="10">
                  <AllowlistedRules userId={userId} />
                </Panel>
                <Panel header="Recent Emails" key="11">
                  <RecentEmails userId={userId} />
                </Panel>

              </PersistedCollapse>
            </Col>
          </Row>
        </Card>
      )}
      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalVisible}
        onOk={enableBackupPayment}
        onCancel={closeModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to Enable Backup Payment?</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>


      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={disableAutoPayModalVisible}
        onOk={disableAutoPay}
        onCancel={closeDisableAutoPayModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to Disable AutoPay ?</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>


      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalForAutopayVisible}
        onOk={forceAutoPay}
        onCancel={closeAutoPayModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to Run AutoPay?</p>
          <p>This will run for both credit card and save & build credit.</p>

          {modalForceAutoPayLoading && <LoaderComponent />}
        </>
      </Modal>


      <Modal
        title={"Block History"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalForHistoryVisible}
        onCancel={closeBlockHistoryModal}
        className="confirm-modal"
        width={"80vw"}
        okButtonProps={{ hidden: true }}
        cancelText="Close"
      >
        <>
          <BlockHistory blockHistory={blockHistory} />
          {blockHistoryLoading && <LoaderComponent />}
        </>
      </Modal>

      <Modal
        title={"Confirm force pull report!"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        okText={"Confirm"}
        onOk={forcefullyPullReport}
        closable={true}
        visible={modalForForcePullVisible}
        onCancel={closeForceReportPullModal}
        className="confirm-modal"
        cancelText="Close"
      >
        <>
          <p>This requests a fresh Experian credit report for the user right away. Please confirm only if it is necessary.</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

      <Modal
        title={"Confirm Experian re-enrollment"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        okText={"Confirm"}
        onOk={reenrollExperianUser}
        closable={true}
        visible={modalForReenrollExperianVisible}
        onCancel={closeReenrollExperianModal}
        className="confirm-modal"
        cancelText="Close"
      >
        <>
          <p>
            Use this when Experian has dropped the user's enrollment and scheduled credit reports have stopped
            (the score in the app is stale). This deletes the user's current Experian subscriber and enrolls them
            again from scratch so that scheduled reports resume.
          </p>
          <p>
            <strong>This does not pull a report by itself.</strong> The next scheduled pull will show whether it
            worked, so check back tomorrow. If a fresh report is needed today, use Force Pull after re-enrolling.
          </p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

      <UpdateAccountModal
        editAccountModalVisible={editAccountModalVisible}
        closeUpdateAccountModal={closeUpdateAccountModal}
        userId={userId}
        userData={data}
        refetchUserData={reloadData}
      />

      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalWhitelistUserSSNVisible}
        onOk={whitelistUserSSN}
        onCancel={closeWhitelistUserSSNModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to whitelist this user's SSN?</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalUndoWhitelistUserSSNVisible}
        onOk={undoWhitelistUserSSN}
        onCancel={closeUndoWhitelistUserSSNModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to undo whitelisting of this user's SSN?</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

    </>
  );
}


const downloadReport = async (userId, reportId) => {
  const rawReportString = await API.getReportFile({ userId, reportId});
  // Turn the string into a blob of type text/plain
  const file = new Blob([rawReportString], { type: "text/plain" });
  const url = window.URL.createObjectURL(file);
  // Now this opens up a new tab showing your JSON/XML text:
  window.open(url, "_blank");
}


export default ProfileSummary;
